import { useState, useEffect, useRef, useCallback } from "react";
import styled, { css } from "styled-components";
import { Box, Flex, Text } from "rebass/styled-components";
import { useSetRecoilState, useRecoilState } from "recoil";
import videojs from "video.js";
import { useInView } from "react-intersection-observer";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { FaPlay } from "react-icons/fa";
import { HiOutlineShieldCheck } from "react-icons/hi";
import { RiShareForwardFill } from "react-icons/ri";

import { trackClipDownload } from "utils";
import { feedPlayer } from "../../../../../globalStorage/autoplay";
import Remix from "components/atoms/form/button/remix";
import { ReactComponent as ClipsEmpty } from "MVVM/Assets/Placeholders/clips-empty.svg";
import { ModalType } from "MVVM/@types";
import { Avatar } from "MVVM/Components/Avatar";
import { Button, MobileDownload, Link } from "MVVM/Components/Button";
import { CopyLink } from "MVVM/Components/Button/Share";
import { DividerAlt } from "MVVM/Components/Divider";
import { useInteractonInterceptor } from "MVVM/Hooks/useInteractionInterceptor";
import { usePortal } from "MVVM/Hooks/usePortal";
import { useUser } from "MVVM/Hooks/useUser";
import { ClipReactions } from "MVVM/Components/ClipReactions";
import { More } from "./MoreClipOptionsDropdown";
import { ClipDownload } from "MVVM/Components/Modals/ClipDownload";
import { EPaywallType } from "MVVM/Components/Modals/Paywall";
import { Body, Title3, Subheader } from "MVVM/Components/Text";
import { MainRail } from "MVVM/Components/Wrappers";
import { AllstarModalState } from "MVVM/State/modals";
import { countClipImpression } from "MVVM/Utilities/Clip";
import {
  AvatarWrapper,
  getAvatarBorderLevel,
  getAvatarImage,
} from "MVVM/Utilities/User";
import { dateFormatWithDistanceSuffix } from "MVVM/Utilities/Clip";
import { VideoPlayer } from "MVVM/VideoPlayer";
import { formatNumber } from "MVVM/Views/Wrapped/util";
import { PinClipButton } from "MVVM/Components/Pinnable/PinClipButton";
import { QuickEdit } from "MVVM/Components/QuickEdit";
import { useClipNavigate } from "MVVM/Hooks/useClipNavigate";
import { ProfileContext } from "MVVM/Views/ClipExperience/@types";

export const Clip = ({
  clip,
  isOwnProfile,
  isFirstClip,
  isFeed,
  setClipInactive,
  hasFiltersSelected,
  user,
}: any) => {
  const { allstarUser } = useUser();
  const videoPlayerRef = useRef<videojs.Player | null>(null);
  const setAllstarModalState = useSetRecoilState(AllstarModalState);
  const username = user?.username || clip?.username || "";
  const avatarImage = getAvatarImage(user);
  const avatarBorderLevel = getAvatarBorderLevel(user);
  const history = useHistory();
  const checkFeatureAccessQuery = useInteractonInterceptor();
  const { navigateToClip } = useClipNavigate();

  const {
    portalOpen: quickEditPortalOpen,
    setPortalOpen: setQuickEditPortalOpen,
    Portal: QuickEditPortal,
  } = usePortal(
    <QuickEdit
      momentId={clip?.moment?.id}
      closeModal={() => setQuickEditPortalOpen(false)}
    />,
    "root",
  );
  const [swappedForVideo, setSwappedForVideo] = useState(false);
  const clipHasBeenPruned = clip?.status === "Pruned";
  const [shareIdPlaying, setShareIdPlaying] = useRecoilState(feedPlayer);
  const [inViewRef, inView] = useInView({
    threshold: 0.5,
    triggerOnce: false,
    rootMargin: "-10% 0px -10% 0px",
  });

  const hasRemix = clip.remixOf && Object.keys(clip.remixOf).length > 0;

  const setRefs = useCallback(
    (node) => {
      // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
      inViewRef(node);
    },
    [inViewRef],
  );

  useEffect(() => {
    if (inView || !isFeed) {
      setSwappedForVideo(true);
      setShareIdPlaying(clip.shareId);
    } else {
      setSwappedForVideo(false);
    }
  }, [inView, isFeed, clip.shareId, setShareIdPlaying]);

  useEffect(() => {
    if (shareIdPlaying !== clip.shareId && shareIdPlaying !== "") {
      setSwappedForVideo(false);
    }
  }, [shareIdPlaying, clip.shareId]);

  const isOwnClip = allstarUser?.user?._id === user?._id;

  const toggleModal = (modalType: ModalType) => {
    setAllstarModalState({
      data: clip,
      functions: {},
      isOpen: modalType,
    });
  };

  const setReportModalOpen = () => {
    setAllstarModalState({
      data: { _id: clip.id, user: { _id: user?._id } },
      functions: {},
      isOpen: ModalType.Report,
    });
  };

  const {
    portalOpen: clipDownloadPortalOpen,
    setPortalOpen: setClipDownloadPortalOpen,
    Portal: ClipDownloadPortal,
  } = usePortal(<ClipDownload clip={clip} clipOwnerId={user?._id} />, "root");

  const handleDownloadClick = async () => {
    const downloadLink = clip.url;

    const { data } = await checkFeatureAccessQuery({
      variables: { feature: "FREE_ENGAGED_DOWNLOAD" },
    });

    if (data.checkFeatureAccess) {
      return setAllstarModalState({
        isOpen: ModalType.Download,
        data: {
          type: EPaywallType.download as EPaywallType,
        },
        functions: {},
      });
    }
    trackClipDownload(clip);
    window.open(downloadLink);
  };

  const setDownloadModalOpen = (isOpen: boolean): void => {
    setAllstarModalState({
      data: clip,
      functions: {},
      isOpen: ModalType.Report,
    });
  };

  const setModalOpen = (isOpen: boolean): void => {
    setAllstarModalState({
      data: clip,
      functions: {},
      isOpen: isOpen ? ModalType.Feedback : ModalType.None,
    });
  };

  const setEditModalOpen = (isOpen: boolean): void => {
    setAllstarModalState({
      data: clip,
      functions: { setClipInactive: setClipInactive },
      isOpen: isOpen ? ModalType.Delete : ModalType.None,
    });
  };

  return (
    <Flex flexDirection="column" style={{ marginBottom: "30px" }}>
      {clipDownloadPortalOpen && ClipDownloadPortal}
      {quickEditPortalOpen && QuickEditPortal}
      <VideoContainer>
        {isOwnProfile && isOwnClip && (
          <PinnedClipContainer
            displayPinnedClip={swappedForVideo}
            clip={clip}
          />
        )}
        <Box
          variant="card"
          name={isFirstClip ? "clip-feed-first-card" : "clip-feed-card"}
        >
          <Box ref={setRefs}>
            <Card
              $swappedForVideo={swappedForVideo}
              snapshot={clip.thumbnailUrl}
              name={
                isFirstClip
                  ? "clip-feed-first-placeholder"
                  : "clip-feed-placeholder"
              }
              $shouldShowSnapshot={!clipHasBeenPruned}
              as={swappedForVideo ? undefined : Link}
              to={swappedForVideo ? null : `/clip?clip=${clip.shareId}`}
            >
              <>
                {swappedForVideo ? (
                  <>
                    <StyledVideoPlayer
                      mp4Source={clip?.url}
                      refForVideoPlayer={videoPlayerRef}
                      onPlayStart={() => countClipImpression(clip.shareId)}
                      loop={true}
                      playAnalyticsProperties={{
                        shareId: clip.shareId,
                        clipId: clip.internalId,
                        clipOwner: user,
                        total_views: clip.views,
                        game: clip.game,
                        isOwnClip: isOwnClip,
                        clipType: clip.type,
                      }}
                      watchAnalyticsProperties={{
                        clipId: clip.internalId,
                        collection: "userProfile",
                        collectionType: hasFiltersSelected
                          ? "Filter"
                          : "Generic",
                        item: clip.shareId,
                        itemType: "Clip",
                        action: "watch",
                        isOwnClip: isOwnClip,
                        clipType: clip.type,
                      }}
                      options={{
                        playsinline: true,
                        autoplay: "play",
                        poster: clip.thumbnailUrl,
                      }}
                    />
                  </>
                ) : (
                  <PlayButton>
                    <PlayContainer variant="play" size={"small"}>
                      <PlayCircle variant="play.spectrum" />
                      <PlayIcon />
                    </PlayContainer>
                  </PlayButton>
                )}
              </>
            </Card>
          </Box>
        </Box>
      </VideoContainer>
      {hasRemix && <RemixedClipInfo clip={clip} />}
      <Flex
        alignItems="center"
        justifyContent="space-between"
        style={{ margin: "1rem 0" }}
      >
        <Flex flexDirection="column">
          <StyledTitle
            variant="link.clipTitle"
            onClick={() =>
              navigateToClip(clip.shareId, {
                type: "profile",
                metadata: { userId: user._id, tab: "recommended" },
              })
            }
          >
            {clip?.title}
          </StyledTitle>
          <Flex style={{ marginTop: "10px", gap: "8px" }}>
            {!!clip.views && (
              <>
                <Text color="chalk" style={{ fontSize: "0.875rem" }}>
                  {formatNumber(clip?.views)} views
                </Text>{" "}
                <Text color="chalk" style={{ fontSize: "0.875rem" }}>
                  &bull;
                </Text>
              </>
            )}
            <Text color="chalk" style={{ fontSize: "0.875rem" }}>
              {dateFormatWithDistanceSuffix(clip?.createdAt)}
            </Text>
          </Flex>
        </Flex>
        <Flex style={{ gap: "8px" }}>
          <StyledClipReactions clip={clip} mobileRight="0px" />
          <Remix
            classification={undefined}
            classificiation={undefined}
            clip={clip}
            disableOnClick={false}
            onClick={() => setQuickEditPortalOpen(true)}
          />
        </Flex>
      </Flex>
      <Flex justifyContent="space-between">
        <Flex alignItems="center" style={{ gap: "16px" }}>
          <Box style={{ transform: "scale(1.1)" }} pt={2} flexShrink={0}>
            <AvatarWrapper size="nav" borderLevel={avatarBorderLevel}>
              <Avatar image={avatarImage} size="nav" />
            </AvatarWrapper>
          </Box>
          <Text variant="link.clipTitle" fontWeight={500} fontSize={20}>
            {username}
          </Text>
        </Flex>
        <Flex style={{ gap: "8px" }}>
          <StyledShareButton
            classification="secondaryRounded"
            onClick={() => toggleModal(ModalType.Share)}
          >
            <StyledShareIcon size={20} />
          </StyledShareButton>
          {allstarUser.loggedIn && (
            <MobileDownload
              size="social"
              clip={clip}
              clipLink={clip.url}
              onClick={() => setClipDownloadPortalOpen(true)}
            />
          )}
          <CopyLink
            clip={clip}
            clipLink={`${window.location.origin}/clip/${clip.shareId}`}
            onClick={undefined}
            isMontage={false}
            isProfile={false}
          ></CopyLink>
          <More
            clip={clip}
            setShareModalOpen={() => toggleModal(ModalType.Share)}
            setReportModalOpen={setReportModalOpen}
            onDownloadClick={handleDownloadClick}
            isOwnClip={isOwnClip}
            isPinned={clip?.isPinned}
            isProfile={true}
            size="social"
            classification={"secondaryRounded"}
            clipLink={`https://${window.location.hostname}/clip?clip=${clip.shareId}`}
            isMontage={false}
            setEditModalOpen={setEditModalOpen}
            setModalOpen={setModalOpen}
            setDownloadModalOpen={setDownloadModalOpen}
          />
        </Flex>
      </Flex>
      <DividerAlt width={["100%"]}></DividerAlt>
    </Flex>
  );
};

export const PinnedClipContainer = ({
  clip,
  displayPinnedClip,
}: {
  clip: any;
  displayPinnedClip: boolean;
}) => {
  return (
    <PinClipWrapper data-testid="pin-clip-button">
      <PinClipButton clipId={clip.id} isVisible={displayPinnedClip} />
    </PinClipWrapper>
  );
};

export const NoFilterResultsContainer = () => {
  return (
    <MainRail pb={[5, 5, 6]}>
      <Flex
        alignItems={"center"}
        justifyContent="center"
        flexDirection="column"
        mb={8}
        mt={6}
      >
        <ClipsEmpty />
        <Title3 my={3}>There are no clips that match your filters</Title3>
        <Body color="chalk">
          You can update or reset your filters above to start again.
        </Body>
      </Flex>
    </MainRail>
  );
};

const RemixedClipInfo = ({ clip }: any) => {
  const history = useHistory();
  const { t } = useTranslation("translation", {
    keyPrefix: "clip.remixClip",
  });
  const avatarBorderLevel = getAvatarBorderLevel(clip?.user);
  const remixClipInfo = clip.remixOf;
  const avatarImage = getAvatarImage(remixClipInfo.user);

  return (
    <RemixClipInfoContainer
      alignItems="center"
      justifyContent="flex-start"
      flexDirection="row"
    >
      <Flex
        ml={2}
        mr={1}
        flexDirection="row"
        alignItems="center"
        justifyContent="flex-start"
        style={{ cursor: "pointer", padding: "10px" }}
      >
        <StyledCheckBadge />
        <Subheader
          ml={2}
          color="chalk"
          shareId={clip.shareId}
          clipId={clip._id}
          onClick={() => {
            history.push(`/clip?clip=${remixClipInfo.shareId}`);
          }}
        >
          {t("body")}
        </Subheader>
      </Flex>
      <AvatarWrapper size="tiny" borderLevel={avatarBorderLevel}>
        <Avatar
          image={avatarImage}
          size="nav"
          to={`/u/${remixClipInfo?.user?.username}`}
        />
      </AvatarWrapper>
      <UserNameLink
        to={`/u/${remixClipInfo?.user?.username}`}
        variant="link.clipTitle"
      >
        {remixClipInfo?.user?.username}
      </UserNameLink>
    </RemixClipInfoContainer>
  );
};

const VideoContainer = styled(Flex)`
  align-items: center;
  flex-grow: 1;
  justify-content: center;
  position: relative;

  ::before {
    background-color: rgba(0, 0, 0, 0.6);
    content: "";
    display: block;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 0;
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }
`;

const StyledShareButton = styled(Button)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.envy};
  display: flex;

  :hover {
    background-color: ${({ theme }) => theme.colors.darkerEnvy};
  }
`;

const StyledShareIcon = styled(RiShareForwardFill)``;

const linkStyle = css<{ truncate?: boolean }>`
  color: inherit;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: ${(props) => {
    return props.truncate ? "nowrap" : "wrap";
  }};

  ${(props) => props.theme.text.title3};
`;

const StyledLink = styled(Link)`
  ${linkStyle}
`;

const StyledTitle = styled(Text)`
  ${linkStyle}

  cursor: pointer;
`;

const StyledClipReactions = styled(ClipReactions)`
  min-height: 40px;
  width: 80px;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    height: 36px;
    width: 65px;
  }
`;

const PinClipWrapper = styled(Box)`
  position: absolute;
  top: 5px;
  left: 5px;
  z-index: 1000;
  cursor: pointer;
`;

const PlayButton = styled(Box)`
  display: none;
`;

const Card = styled(Box)<{
  snapshot: string;
  $shouldShowSnapshot: boolean;
  $swappedForVideo: boolean;
}>`
  display: block;
  padding-top: 56.25%;
  position: relative;
  overflow: hidden;
  height: 0;
  ${(props) =>
    !props.$swappedForVideo
      ? `background-image: url("${props.snapshot}"), url("https://media.allstar.gg/static/fallback.jpg")`
      : null};
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
`;

const PlayCircle = styled(Box)`
  width: 100%;
  backface-visibility: hidden;
  border: 6px;
  border-style: solid;
  border-radius: 50%;
  border-color: ${(props) => props.theme.colors.midnightAlpha90};
`;

const PlayContainer = styled(Box)<{ size: string }>`
  ${({ size, theme }) => (size ? { ...theme.play[size] } : null)}

  &:hover ${PlayCircle} {
    ${({ theme }) => ({ ...theme.play.spectrum[":hover"] })}
  }
`;

const PlayIcon = styled(FaPlay)`
  width: 43.75%;
  height: 43.75%;
  position: absolute;
  top: 50%;
  left: 56.25%;
  transform: translateX(-50%) translateY(-50%);
`;

const StyledVideoPlayer = styled(VideoPlayer)`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;

  .video-js {
    background-color: transparent;
  }

  .vjs-big-play-button {
    display: none;
  }
`;

const StyledCheckBadge = styled(HiOutlineShieldCheck)`
  color: ${(props) => props.theme.colors.envy};
  width: 20px;
  height: 20px;
`;

const RemixClipInfoContainer = styled(Flex)`
  width: 100%;
  border-radius: 0px 0px 8px 8px;
  height: 60px;
  background: rgba(12, 19, 24, 0.5);
`;

const UserNameLink = styled(Link)`
  font-size: 1.125em;
  margin-left: 10px;
`;
